"use client";

import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";

/**
 * Visual heartbeat indicator showing generation liveness.
 * Green pulse (<90s), yellow (90-180s), red (>180s).
 */
export function HeartbeatPulse({ heartbeatIso }: { heartbeatIso: string | null }) {
  const [nowMs, setNowMs] = useState<number | null>(null);
  useEffect(() => {
    if (!heartbeatIso) {
      setNowMs(null);
      return;
    }
    setNowMs(Date.now());
    const id = setInterval(() => setNowMs(Date.now()), 5000);
    return () => clearInterval(id);
  }, [heartbeatIso]);

  if (!heartbeatIso || nowMs === null) return null;

  const ageSec = Math.floor((nowMs - new Date(heartbeatIso).getTime()) / 1000);

  const isAlive = ageSec < 90;
  const isStale = ageSec >= 90 && ageSec < 180;

  const color = isAlive ? "bg-emerald-400" : isStale ? "bg-amber-400" : "bg-red-400";
  const ringColor = isAlive ? "bg-emerald-400/30" : isStale ? "bg-amber-400/30" : "bg-red-400/30";
  const label = isAlive ? "System active" : isStale ? "Waiting for response..." : "Connection lost";
  const textColor = isAlive
    ? "text-emerald-400/60"
    : isStale
    ? "text-amber-400/60"
    : "text-red-400/60";

  return (
    <div className="flex items-center gap-2">
      <div className="relative flex items-center justify-center">
        {isAlive && (
          <span className={cn("absolute size-3 rounded-full animate-ping", ringColor)} />
        )}
        <span className={cn("relative size-2 rounded-full", color)} />
      </div>
      <span className={cn("text-[10px] font-medium", textColor)}>
        {label}
      </span>
    </div>
  );
}
